import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    BarChart3,
    TrendingUp,
    TrendingDown,
    DollarSign,
    Target,
    Clock,
    Users,
    Activity,
    ArrowRight,
    Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCrmOrderSummary } from '@/api/orders/use-crm-orders';
import { useTradeHistorySummary } from '@/api/trade-history/use-crm-trade-history';
import { useTradingPermissions } from '@/hooks/useTradingPermissions';
import { TradingAnalyticsGate } from '@/components/TradingPermissionGate';

interface TradingMetricsWidgetProps {
    className?: string;
}

const TradingMetricsWidget: React.FC<TradingMetricsWidgetProps> = ({ className }) => {
    const navigate = useNavigate();
    const { tradePermissions } = useTradingPermissions();

    // Fetch trading statistics
    const { data: orderSummary, isLoading: orderSummaryLoading } = useCrmOrderSummary('30d');
    const { data: tradeHistoryStats, isLoading: tradeHistoryLoading } = useTradeHistorySummary({
        from_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });

    const isLoading = orderSummaryLoading || tradeHistoryLoading;

    // Calculate combined metrics
    const totalOrders = orderSummary?.total_orders || 0;
    const filledOrders = orderSummary?.filled_orders || 0;
    const pendingOrders = orderSummary?.pending_orders || 0;
    const cancelledOrders = orderSummary?.cancelled_orders || 0;
    const totalVolume = orderSummary?.total_volume || 0;
    const totalFees = orderSummary?.total_fees || 0;
    const orderWinRate = orderSummary?.win_rate || 0;

    const totalTrades = tradeHistoryStats?.total_trades || 0;
    const profitableTrades = tradeHistoryStats?.profitable_trades || 0;
    const losingTrades = tradeHistoryStats?.losing_trades || 0;
    const tradeWinRate = tradeHistoryStats?.win_rate || 0;
    const totalPnL = tradeHistoryStats?.total_pnl || 0;
    const grossProfit = tradeHistoryStats?.gross_profit || 0;
    const grossLoss = tradeHistoryStats?.gross_loss || 0;

    // Calculate order fill rate
    const fillRate = totalOrders > 0 ? (filledOrders / totalOrders) * 100 : 0;

    // Calculate average trade size
    const averageTradeSize = totalTrades > 0 ? totalVolume / totalTrades : 0;

    // Calculate profit factor
    const profitFactor = grossLoss !== 0 ? grossProfit / Math.abs(grossLoss) : 0;

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatNumber = (num: number, decimals: number = 2) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(num);
    };

    const getPnLColor = (pnl: number) => {
        if (pnl > 0) return 'text-green-600';
        if (pnl < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    const getTrendIcon = (value: number) => {
        if (value > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
        if (value < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
        return <Activity className="h-4 w-4 text-gray-500" />;
    };

    if (!tradePermissions.view) {
        return null;
    }

    return (
        <TradingAnalyticsGate>
            <div className={`space-y-6 ${className}`}>
                {/* Trading Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                            <BarChart3 className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <>
                                    <div className="text-2xl font-bold">{totalOrders}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Badge variant="secondary" className="text-xs">
                                            {filledOrders} filled
                                        </Badge>
                                        <Badge variant="outline" className="text-xs">
                                            {pendingOrders} pending
                                        </Badge>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Trading Volume</CardTitle>
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <>
                                    <div className="text-2xl font-bold">
                                        {formatCurrency(totalVolume)}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {formatNumber(averageTradeSize)} avg per trade
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total PnL</CardTitle>
                            {getTrendIcon(totalPnL)}
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <>
                                    <div className={`text-2xl font-bold ${getPnLColor(totalPnL)}`}>
                                        {formatCurrency(totalPnL)}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Profit Factor: {formatNumber(profitFactor, 2)}
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Win Rate</CardTitle>
                            <Target className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <>
                                    <div className="text-2xl font-bold">
                                        {formatNumber(tradeWinRate, 1)}%
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {profitableTrades}W / {losingTrades}L
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Detailed Trading Metrics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Order Performance */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5" />
                                Order Performance
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {isLoading ? (
                                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                            ) : (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Fill Rate
                                        </span>
                                        <span className="text-lg font-bold">
                                            {formatNumber(fillRate, 1)}%
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2">
                                                <Badge className="bg-green-100 text-green-800">
                                                    Filled
                                                </Badge>
                                            </span>
                                            <span className="font-medium">{filledOrders}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2">
                                                <Badge variant="secondary">Pending</Badge>
                                            </span>
                                            <span className="font-medium">{pendingOrders}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2">
                                                <Badge variant="destructive">Cancelled</Badge>
                                            </span>
                                            <span className="font-medium">{cancelledOrders}</span>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">
                                                Total Fees
                                            </span>
                                            <span className="font-bold">
                                                {formatCurrency(totalFees)}
                                            </span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full"
                                        onClick={() => navigate('/trading-history')}
                                    >
                                        View All Orders
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Trade Analysis */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Activity className="h-5 w-5" />
                                Trade Analysis
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {isLoading ? (
                                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-green-600">
                                                {formatCurrency(grossProfit)}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                Gross Profit
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-red-600">
                                                {formatCurrency(Math.abs(grossLoss))}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                Gross Loss
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">
                                                Average Win
                                            </span>
                                            <span className="font-medium">
                                                {formatCurrency(
                                                    tradeHistoryStats?.average_win || 0
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">
                                                Average Loss
                                            </span>
                                            <span className="font-medium">
                                                {formatCurrency(
                                                    Math.abs(tradeHistoryStats?.average_loss || 0)
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">
                                                Largest Win
                                            </span>
                                            <span className="font-medium text-green-600">
                                                {formatCurrency(
                                                    tradeHistoryStats?.largest_profit || 0
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">
                                                Largest Loss
                                            </span>
                                            <span className="font-medium text-red-600">
                                                {formatCurrency(
                                                    Math.abs(tradeHistoryStats?.largest_loss || 0)
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full"
                                        onClick={() => navigate('/trading-history')}
                                    >
                                        View Trade History
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Trading Quick Actions
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Button
                                variant="outline"
                                className="h-auto p-4 flex flex-col items-center gap-2"
                                onClick={() => navigate('/trading-history')}
                            >
                                <BarChart3 className="h-6 w-6" />
                                <div className="text-center">
                                    <div className="font-medium">Manage Orders</div>
                                    <div className="text-xs text-muted-foreground">
                                        View and manage all orders
                                    </div>
                                </div>
                            </Button>
                            <Button
                                variant="outline"
                                className="h-auto p-4 flex flex-col items-center gap-2"
                                onClick={() => navigate('/trading-history')}
                            >
                                <Activity className="h-6 w-6" />
                                <div className="text-center">
                                    <div className="font-medium">Trade History</div>
                                    <div className="text-xs text-muted-foreground">
                                        Analyze trading performance
                                    </div>
                                </div>
                            </Button>
                            <Button
                                variant="outline"
                                className="h-auto p-4 flex flex-col items-center gap-2"
                                onClick={() => navigate('/customers')}
                            >
                                <Users className="h-6 w-6" />
                                <div className="text-center">
                                    <div className="font-medium">Customer Trading</div>
                                    <div className="text-xs text-muted-foreground">
                                        Manage customer accounts
                                    </div>
                                </div>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </TradingAnalyticsGate>
    );
};

export default TradingMetricsWidget;
